import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Link,
  Stack,
  IconButton,
  InputAdornment,
  Checkbox,
  Card,
  CardContent,
  CardHeader,
  Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LoadingButton } from "@mui/lab";
import { Formik, Field } from "formik";
import { useTranslation } from "react-i18next";
import Iconify from "../../components/Iconify";
import { DraftLogin } from "../../pages/api";
import { useLoginMutation } from "../../pages/login/loginSlice";
import Schema from "./Schema";
import { Message } from "../../models";
import { shadow } from "../../utils/cssStyles";
import { toErrorMsg } from "../../utils";
import TextField from "../../components/TextField";

export default function LoginForm() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const [login] = useLoginMutation();
  const initialValues: DraftLogin = { userName: "", password: "" };
  const [message, setMessage] = useState<Message | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={Schema(t)}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={async (values, actions) => {
          try {
            setMessage(null);
            const { appUser, error, invalidData }: any =
              await login(values).unwrap();
            actions.setSubmitting(false);
            if (appUser) {
              if (appUser.user?.clients?.length > 0) {
                window.location.href = "/";
              } else {
                setMessage({
                  type: "error",
                  message: t("Donothaveaccesstobranches"),
                });
              }
            }
            if (error) {
              setMessage({ type: "error", message: toErrorMsg(error) });
            }
            if (invalidData) {
              actions.setErrors(invalidData);
              setMessage({ type: "error", message: t("Pleasecorrecterrors2") });
            }
          } catch (error: any) {
            setMessage({ type: "error", message: error.message });
          }
        }}
      >
        {(props) => (
          <form onSubmit={props.handleSubmit}>
            <Card style={shadow}>
              <CardHeader
                title={t("Signinto")}
                subheader={
                  process.env.REACT_APP_ENV === "DEMO" ? (
                    <Grid sx={{ color: "#ff5a00" }}>
                      <div style={{ marginBottom: "5px" }}>
                        {t("UsethebelowCredientialstologin")}
                      </div>
                      <div>
                        <span style={{ fontWeight: "bold" }}>
                          {t("UserName")}:{" "}
                        </span>
                        <span>user</span>
                      </div>
                      <div>
                        <span style={{ fontWeight: "bold" }}>
                          {t("Password")}:{" "}
                        </span>
                        <span>user@123</span>
                      </div>
                    </Grid>
                  ) : (
                    <>
                      {t("Donthaveanaccount")} {""}
                      <Link
                        variant="subtitle2"
                        sx={{ cursor: "pointer" }}
                        onClick={() => navigate("/register")}
                      >
                        {t("Getstarted")}
                      </Link>
                    </>
                  )
                }
              />
              <CardContent sx={{ pt: 5 }}>
                <Stack spacing={3}>
                  <Field
                    name="userName"
                    render={({ field }: any) => (
                      <TextField
                        field={field}
                        props={props}
                        name="userName"
                        label={t("UserName")}
                        placeholder={t("EnterUserName")}
                        // required={true}
                        autoFocus={true}
                        error={!!props?.errors?.userName}
                        helperText={props?.errors?.userName}
                      />
                    )}
                  />

                  <Field
                    name="password"
                    render={({ field }: any) => (
                      <TextField
                        field={field}
                        props={props}
                        name="password"
                        label={t("Password")}
                        type={showPassword ? "text" : "password"}
                        placeholder={t("EnterPassword")}
                        // required={true}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                              >
                                <Iconify
                                  icon={
                                    showPassword
                                      ? "eva:eye-fill"
                                      : "eva:eye-off-fill"
                                  }
                                />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        error={!!props?.errors?.password}
                        helperText={props?.errors?.password}
                        hasVS={false}
                      />
                    )}
                  />
                </Stack>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ my: 2 }}
                >
                  <span>
                    <Checkbox name="remember" />
                    <span>{t("Rememberme")}</span>
                  </span>
                  <Link variant="body2" sx={{ mb: 5 }}>
                    {process.env.REACT_APP_ENV !== "DEMO" && (
                      <Link
                        variant="subtitle2"
                        sx={{ cursor: "pointer" }}
                        onClick={() => navigate("/fpass")}
                      >
                        {t("Forgotpassword")}
                      </Link>
                    )}
                  </Link>
                </Stack>

                <LoadingButton
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="small"
                  loading={props.isSubmitting}
                  // disabled={props.isSubmitting}
                  // onClick={handleClick}
                >
                  {t("Login")}
                </LoadingButton>
                {message?.message && (
                  <div
                    style={{
                      marginTop: "15px",
                      color: theme.palette.error.main,
                    }}
                  >
                    {message.message}
                  </div>
                )}
              </CardContent>
            </Card>
          </form>
        )}
      </Formik>
    </>
  );
}
